import React, { useEffect, useRef } from 'react';
import useAppModal from '@/hooks/useAppModal';
import { APP_VERSION_MUTATION_SUCCESS_EVENT } from '@/shared/lib/appVersionEvents';

const APP_VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const APP_VERSION_UPDATE_MESSAGE =
  'システムが更新されました。\n最新バージョンを反映するため、画面を再読み込みしてください。';

interface AppVersionRouter {
  state: {
    location: {
      pathname: string;
      search: string;
      hash: string;
    };
  };
  subscribe: (listener: (state: AppVersionRouter['state']) => void) => () => void;
}

interface AppVersionWatcherProps {
  hasFormChanges: boolean;
  router?: AppVersionRouter;
}

/**
 * Watches the deployed frontend version and asks the user to reload when a new build is available.
 *
 * The check is production-only and reads nginx-served /version.json. Docker writes that file during
 * image build, while nginx marks it no-store so browser cache does not hide releases.
 */
const AppVersionWatcher: React.FC<AppVersionWatcherProps> = ({ hasFormChanges, router }) => {
  const appModal = useAppModal();
  const appVersionRef = useRef<string | null>(null);
  const pendingVersionRef = useRef<string | null>(null);
  const mutationSuccessPendingRef = useRef(false);
  const hasFormChangesRef = useRef(hasFormChanges);
  const modalShownRef = useRef(false);
  const showReloadModalRef = useRef<() => void>(() => {});

  useEffect(() => {
    hasFormChangesRef.current = hasFormChanges;
  }, [hasFormChanges]);

  useEffect(() => {
    if (!import.meta.env.PROD || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    let timerId: number | undefined;
    let stopped = false;

    const scheduleNextCheck = () => {
      if (stopped || modalShownRef.current) return;
      timerId = window.setTimeout(checkVersion, APP_VERSION_CHECK_INTERVAL_MS);
    };

    const showReloadModal = () => {
      if (modalShownRef.current) return;
      modalShownRef.current = true;
      if (timerId !== undefined) {
        window.clearTimeout(timerId);
      }

      appModal.warning({
        content: <div style={{ whiteSpace: 'pre-line' }}>{APP_VERSION_UPDATE_MESSAGE}</div>,
        okText: 'OK',
        closable: false,
        maskClosable: false,
        keyboard: false,
        onOk: () => {
          window.location.reload();
        },
      });
    };
    showReloadModalRef.current = showReloadModal;

    async function checkVersion() {
      if (stopped || modalShownRef.current) return;

      if (document.visibilityState !== 'visible') {
        scheduleNextCheck();
        return;
      }

      try {
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          scheduleNextCheck();
          return;
        }

        const data = (await response.json()) as { version?: unknown };
        const nextVersion = typeof data.version === 'string' ? data.version : '';
        if (!nextVersion) {
          scheduleNextCheck();
          return;
        }

        if (appVersionRef.current === null) {
          appVersionRef.current = nextVersion;
          scheduleNextCheck();
          return;
        }

        if (appVersionRef.current !== nextVersion) {
          pendingVersionRef.current = nextVersion;
          return;
        }
      } catch {
        // Version checks are best-effort; network/API errors are handled elsewhere.
      }

      scheduleNextCheck();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (timerId !== undefined) {
          window.clearTimeout(timerId);
        }
        void checkVersion();
      }
    };

    void checkVersion();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopped = true;
      if (timerId !== undefined) {
        window.clearTimeout(timerId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [appModal]);

  useEffect(() => {
    if (
      !import.meta.env.PROD ||
      hasFormChanges ||
      modalShownRef.current ||
      !mutationSuccessPendingRef.current
    ) {
      return;
    }

    if (pendingVersionRef.current) {
      mutationSuccessPendingRef.current = false;
      showReloadModalRef.current();
    }
  }, [hasFormChanges]);

  useEffect(() => {
    if (!import.meta.env.PROD || typeof window === 'undefined') return;

    const handleMutationSuccess = () => {
      if (!pendingVersionRef.current || modalShownRef.current) return;

      if (hasFormChangesRef.current) {
        mutationSuccessPendingRef.current = true;
        return;
      }

      showReloadModalRef.current();
    };

    window.addEventListener(APP_VERSION_MUTATION_SUCCESS_EVENT, handleMutationSuccess);
    return () => {
      window.removeEventListener(APP_VERSION_MUTATION_SUCCESS_EVENT, handleMutationSuccess);
    };
  }, []);

  useEffect(() => {
    if (!import.meta.env.PROD || !router) return;

    let previousLocationKey = [
      router.state.location.pathname,
      router.state.location.search,
      router.state.location.hash,
    ].join('');

    return router.subscribe((state) => {
      const nextLocationKey = [
        state.location.pathname,
        state.location.search,
        state.location.hash,
      ].join('');

      if (nextLocationKey === previousLocationKey) return;
      previousLocationKey = nextLocationKey;

      if (!pendingVersionRef.current || modalShownRef.current) {
        return;
      }

      showReloadModalRef.current();
    });
  }, [router]);

  return null;
};

export default AppVersionWatcher;
